import { useEffect, useState } from "react";
import { GraduationCap, BadgeCheck, UserRound } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { api } from "@/lib/api";

export const Team = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/team")
      .then((r) => setMembers(r.data))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="equipe" data-testid="team-section" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          center
          overline="Nossa equipe"
          title="Profissionais dedicados ao seu cuidado"
          text="Uma equipe comprometida com a sua saúde, mobilidade e qualidade de vida."
        />

        {loading ? (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-[420px] rounded-2xl bg-cob-beige/60 animate-pulse" />
            ))}
          </div>
        ) : members.length === 0 ? (
          <p className="mt-14 text-center text-cob-muted" data-testid="team-empty-state">
            Em breve, conheça nossa equipe.
          </p>
        ) : (
          <div
            className={
              members.length < 3
                ? "mt-14 flex flex-wrap justify-center gap-8 max-w-5xl mx-auto"
                : "mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto"
            }
          >
            {members.map((member, i) => (
              <Reveal key={member.id} delay={i * 0.1} className={members.length < 3 ? "w-full max-w-sm" : "h-full"}>
                <article
                  data-testid={`team-card-${i + 1}`}
                  className="group h-full overflow-hidden rounded-2xl border border-cob-light/70 bg-white shadow-[0_12px_40px_-22px_rgba(6,63,50,0.25)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_55px_-24px_rgba(6,63,50,0.4)]"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-cob-light/40">
                    {member.photo ? (
                      <img loading="lazy" decoding="async"
                        src={member.photo}
                        alt={member.name}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-cob-medium">
                        <UserRound className="h-20 w-20" strokeWidth={1} />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-cob-dark/50 to-transparent" />
                  </div>
                  <div className="p-6">
                    <span className="block h-px w-10 bg-cob-gold" />
                    <h3 className="mt-3 font-serif text-2xl font-semibold text-cob-dark">{member.name}</h3>
                    {member.role && (
                      <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.16em] text-cob-gold">
                        {member.role}
                      </p>
                    )}
                    {member.registry && (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-cob-muted">
                        <BadgeCheck className="h-4 w-4 text-cob-green" /> {member.registry}
                      </p>
                    )}
                    {member.education && (
                      <p className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] text-cob-muted">
                        <GraduationCap className="h-4 w-4 text-cob-green" /> {member.education}
                      </p>
                    )}
                    {member.bio && (
                      <p className="mt-3 text-sm leading-relaxed text-cob-muted">{member.bio}</p>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
